/**
 * eSIM lock state — "can this SIM ever be put on a different modem?"
 *
 * A profile whose activation code cannot be downloaded again is tied, for
 * good, to the modem (IMEI) it was first downloaded to: an eUICC holds a
 * handful of profiles, the platform keeps the profile in place, and the only
 * way to "move" it is to delete it — after which the carrier refuses the
 * re-download. That is a property customers know when they add the SIM, so
 * they can declare it (`locked: true`); the platform also derives it from the
 * download budget.
 *
 * `getESimLockInfo` is display / pre-flight logic. `esimRelocationPolicy` is
 * what the dispatcher reads before moving a profile between modems (#375).
 */
const isInstalled = (p) => !!(p.installedOn && p.installedOn.imei);
/**
 * Downloads really spent. A profile that sits on a card was downloaded at
 * least once, whatever `downloadCount` says: the counter is only incremented
 * when a test run that downloaded the profile succeeds, so a profile installed
 * from the eUICC menu, or whose first run failed, still reads 0.
 */
export function effectiveDownloadCount(p) {
    return Math.max(p.downloadCount || 0, isInstalled(p) ? 1 : 0);
}
/** Downloads left on the effective count; null = unlimited. */
export function effectiveDownloadsLeft(p) {
    const limit = p.downloadLimit || 'unlimited';
    const used = effectiveDownloadCount(p);
    if (limit === 'unlimited')
        return null;
    if (limit === 'single_use')
        return Math.max(0, 1 - used);
    if (limit === 'limited')
        return Math.max(0, (p.maxDownloads ?? 0) - used);
    return 0;
}
export function getESimLockInfo(p) {
    const installed = isInstalled(p);
    const left = effectiveDownloadsLeft(p);
    const counterLooksStale = installed && (p.downloadCount || 0) < 1 && (p.downloadLimit || 'unlimited') !== 'unlimited';
    const budgetSpent = left === 0;
    const base = { downloadsLeft: left, counterLooksStale };
    if (p.shared) {
        return {
            ...base,
            state: installed ? 'locked' : 'spent',
            source: 'shared_sim',
            canUnlock: false,
            label: installed ? 'Locked' : 'No modem on record',
            detail: installed
                ? 'Shared resident SIM. It is never downloaded, so it only works in the modem it sits in.'
                : 'Shared resident SIM with no modem on record. Re-mark it shared from the modem\'s eUICC menu.',
        };
    }
    if (installed && (budgetSpent || p.locked)) {
        return {
            ...base,
            state: 'locked',
            source: budgetSpent ? 'download_budget' : 'customer',
            canUnlock: !budgetSpent,
            label: 'Locked',
            detail: budgetSpent
                ? 'No downloads left. This SIM only works in the modem it is installed on; deleting it from that modem loses it for good.'
                : 'Locked by your organization. This SIM stays in the modem it is installed on.',
        };
    }
    if (!installed && budgetSpent) {
        return {
            ...base,
            state: 'spent',
            source: 'download_budget',
            canUnlock: false,
            label: 'Spent',
            detail: 'All downloads are used and the SIM is not on any modem, so it cannot run. Raise the download limit if the carrier allows another download.',
        };
    }
    if (!installed && (p.locked || left === 1)) {
        return {
            ...base,
            state: 'locks_on_install',
            source: p.locked ? 'customer' : 'download_budget',
            canUnlock: !!p.locked && left !== 1,
            label: 'Locks on install',
            detail: left === 1
                ? 'One download left. The first modem it is downloaded to keeps it for good.'
                : 'Marked as not re-downloadable. The first modem it is downloaded to keeps it.',
        };
    }
    return {
        ...base,
        state: 'redownloadable',
        source: null,
        canUnlock: false,
        label: 'Re-downloadable',
        detail: left === null ? 'Can be downloaded again, so it can run on any capable modem.' : `${left} download${left === 1 ? '' : 's'} left. It can still be moved to another modem.`,
    };
}
/** True when the SIM can only ever run on the modem it is installed on. */
export function isESimLockedToModem(p) {
    return getESimLockInfo(p).state === 'locked';
}
export function esimRelocationPolicy(p) {
    if (p.shared === true || p.locked === true)
        return 'never';
    if ((p.downloadLimit || 'unlimited') === 'unlimited')
        return 'free';
    if (p.downloadLimit === 'limited' && (effectiveDownloadsLeft(p) ?? 0) > 0)
        return 'needs_approval';
    return 'never';
}
